import { Popover } from '@headlessui/react';
import _ from 'lodash';
import { ReactNode, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { GoChevronRight } from 'react-icons/go';
import { IoMove } from 'react-icons/io5';
import { MdDragIndicator } from 'react-icons/md';
import { PiNotePencil, PiTrash } from 'react-icons/pi';
import { Tooltip } from 'react-tooltip';
import { v4 as uuid } from 'uuid';

import cn from 'app/utils/ClassName';

import Button from '../Button';

import { SortListItem } from './SortListItem';

interface Props<T> {
  title?: string;
  listItems: T[];
  type: string;
  isChanged: boolean;
  createButtonText?: string;
  emptyResultComponent?: ReactNode;
  onUpdateOrder: () => void;
  onCreate?: () => void;
  onEdit?: (listItem: T) => void;
  onDelete?: (listItem: T) => void;
  setNewOrder: (newOrderedList: T[]) => void;
  onRowSelect?: (listItem: T) => void;
  CustomListItemComponent?: ({ item }:{ item: T }) => ReactNode;
  nameExtractor?: (listItem: T) => string;
  isDisableTop?: boolean;
  isHideEmptyText?: boolean;
  isDisableDrag?: boolean;
  containerClassName?: string;
  isDisableCreateButton?: boolean;
  isDisableMoveToRowPopOver?: boolean;
  customEmptyText?: string;
}

// eslint-disable-next-line @typescript-eslint/comma-dangle
export const SortList = <T,>({
  title,
  listItems,
  type,
  isChanged,
  createButtonText,
  emptyResultComponent,
  onUpdateOrder,
  onCreate,
  onEdit,
  onDelete,
  setNewOrder,
  onRowSelect,
  CustomListItemComponent,
  nameExtractor,
  isDisableTop = false,
  isHideEmptyText = false,
  isDisableDrag = false,
  isDisableCreateButton = false,
  containerClassName,
  isDisableMoveToRowPopOver,
  customEmptyText,
}: Props<T>) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const onSort = (draggedRowIndex: number, targetRowIndex: number) => {
    if (setNewOrder) {
      const tempUserLst = _.cloneDeep(listItems);
      const draggedRow = tempUserLst.splice(draggedRowIndex, 1)[0];
      tempUserLst.splice(targetRowIndex, 0, draggedRow);

      setNewOrder(tempUserLst);
    }
  };

  return (
    <div className={cn(
      'flex size-full flex-col overflow-y-auto pb-[38px]',
      containerClassName,
    )}
    >
      { !isDisableTop ? (
        <div className="flex w-full flex-row items-center justify-between border-b border-border-brand px-[16px] py-[12px] text-text-primary">
          <div className="flex w-full flex-row justify-between gap-1">
            {title
              ? <div className="text-text-primary">{title}</div>
              : <div />}
            <div className="flex">
              {onCreate ? (
                <Button
                  variant="text"
                  color="primary"
                  containerStyle={cn(
                    'flex h-6 items-center justify-center px-2 text-[14px]',
                    'hover:bg-bg-primary',
                    isDisableCreateButton ? 'opacity-50 hover:bg-bg-white' : '',
                  )}
                  onClick={() => onCreate()}
                  disabled={isDisableCreateButton}
                >
                  {createButtonText || '新增'}
                </Button>
              ) : null}
              {!isDisableDrag ? (
                <Button
                  variant="text"
                  color="primary"
                  disabled={!isChanged}
                  onClick={() => {
                    onUpdateOrder();
                  }}
                  containerStyle={cn(
                    'flex h-6 items-center justify-center px-2 text-[14px]',
                    'hover:bg-bg-primary',
                    !isChanged ? 'opacity-50 hover:bg-bg-primary' : '',
                  )}
                >
                  保存排序
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      <div className={cn(
        'flex w-full flex-1 flex-col gap-[12px] overflow-y-auto',
        !isDisableTop ? 'p-[16px]' : '',
      )}
      >
        <DndProvider backend={HTML5Backend}>
          {listItems.length
            ? listItems.map((item, index) => {
              const uniqueId = uuid();
              return (
                <SortListItem
                  key={`row-${index}`}
                  id={uniqueId}
                  index={index}
                  reorderRow={onSort}
                  type={type}
                  disableDrag={isDisableDrag}
                >
                  <div className="flex size-full items-center justify-between bg-bg-primary p-[12px] text-text-primary">
                    <div className="flex w-full items-center gap-x-[8px]">
                      {!isDisableDrag && (
                        <MdDragIndicator className="size-4 min-w-[16px] cursor-move text-text-primary" />
                      )}
                      {CustomListItemComponent ? <CustomListItemComponent item={item} /> : null}

                      {nameExtractor ? (
                        <div className={cn(
                          'mt-px break-all text-text-primary',
                          isDisableDrag ? 'pl-[8px]' : '',
                        )}
                        >
                          {nameExtractor?.(item)}
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-row">
                      {!isDisableDrag && !isDisableMoveToRowPopOver ? (
                        <Popover as="div" className="relative" data-tooltip-id="faq-category-move">
                          <Popover.Button
                            as={Button}
                            variant="text"
                            color="primary"
                            containerStyle={cn(
                              'flex size-8 items-center justify-center rounded-full',
                            )}
                          >
                            <IoMove className="size-5 text-text-primary" />
                          </Popover.Button>
                          <Popover.Panel
                            focus
                            className="absolute right-0 z-[5] mt-[8px] flex w-[210px] items-center gap-[8px] border border-border-brand bg-white px-[16px] py-[8px] "
                          >
                            {({ close }) => (
                              <form
                                className="flex items-center gap-[8px]"
                                onSubmit={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                              >
                                <div className="shrink-0">移至</div>
                                <input
                                  ref={inputRef}
                                  type="number"
                                  className={cn(
                                    'w-[60px] rounded-[6px] border-[1.24px] bg-white px-[8px] py-[4px] focus:outline-none focus:ring-0 flex-1',
                                    '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                                  )}
                                  onKeyDown={(e) => {
                                    if (['e', 'E', '+', '-'].includes(e.key)) { e.preventDefault(); }
                                  }}
                                  onWheel={type === 'number' ? (e) => { e.currentTarget.blur(); } : undefined}
                                />
                                列
                                <Button
                                  variant="text"
                                  type="submit"
                                  color="primary"
                                  containerStyle="px-[8px] py-[4px] shrink-0"
                                  onClick={() => {
                                    if (inputRef.current?.value && Number(inputRef.current.value) >= 1) {
                                      const maxPosition = Math.min(Number(inputRef.current.value), listItems.length);
                                      const newSortList = [...listItems];
                                      newSortList.splice(index, 1);
                                      newSortList.splice(maxPosition - 1, 0, item);
                                      setNewOrder(newSortList);
                                      close();
                                    }
                                  }}
                                >
                                  確認
                                </Button>
                              </form>
                            )}
                          </Popover.Panel>
                        </Popover>
                      ) : null}
                      <Tooltip
                        id="faq-category-move"
                        place="top"
                        content="移至"
                      />
                      {onEdit ? (
                        <>
                          <Button
                            variant="text"
                            color="primary"
                            containerStyle={cn(
                              'flex size-8 items-center justify-center rounded-full',
                            )}
                            onClick={() => {
                              onEdit(item);
                            }}
                          >
                            <PiNotePencil className="size-5 text-text-primary" />
                          </Button>
                          <Tooltip
                            id="faq-category-edit"
                            place="top"
                            content="編輯"
                          />
                        </>
                      ) : null}
                      {onDelete ? (
                        <>
                          <Button
                            variant="text"
                            color="primary"
                            containerStyle={cn(
                              'flex size-8 items-center justify-center rounded-full',
                            )}
                            onClick={() => {
                              onDelete(item);
                            }}
                          >
                            <PiTrash className="size-5 text-text-primary" />
                          </Button>
                          <Tooltip
                            id="faq-category-delete"
                            place="top"
                            content="刪除"
                          />
                        </>
                      ) : null}
                      {onRowSelect ? (
                        <Button
                          variant="text"
                          color="primary"
                          containerStyle={cn(
                            'flex size-8 items-center justify-center rounded-full',
                            'border-text-light hover:border hover:bg-bg-primary',
                          )}
                          onClick={() => {
                            onRowSelect(item);
                          }}
                        >
                          <GoChevronRight className="size-5 text-text-primary" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </SortListItem>
              );
            })
            : null}
          {!listItems.length && emptyResultComponent
            ? emptyResultComponent
            : null}

          {!listItems.length && !emptyResultComponent ? (
            <div className="flex flex-1 flex-col items-center justify-center text-text-secondary">
              {!isHideEmptyText ? <p>{customEmptyText || '未能找到任何結果'}</p> : null}
            </div>
          ) : null}
        </DndProvider>
      </div>
    </div>
  );
};
